import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { faTag, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  Checkbox,
  Modal,
  Table,
  useToast
} from '@morressier/alembic';
import ListLoader from 'src/components/ListLoader';
import ListView from 'src/layouts/ListView';
import { useForm, Controller } from 'react-hook-form';
import { updateUserTag } from 'src/api/mutations';
import { useState } from 'react';
import { ITags } from '@publishing-engine/domain/types/AuthorType';
import { ITags as IWorkspaceTag } from '@publishing-engine/domain/models';
interface Props {
  isOpen: boolean;
  onClose: () => void;
  data: IWorkspaceTag[];
  allTags: ITags[];
  orgId?: string;
  userId?: string;
}

const AddingUserTagsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  data,
  allTags,
  orgId = '',
  userId = ''
}) => {
  const { handleSubmit, control, watch, setValue } = useForm({
    defaultValues: {
      tags: allTags?.map(tag => ({ id: tag.id, name: tag.name })) // Default checked tags as objects
    }
  });
  const toast = useToast();
  const selectedTags = watch('tags'); // Watch the selected tag IDs
  const [deleteList, setDeleteList] = useState<
    Array<{ id: string; name: string }>
  >([]); // Objects with id and name
  const [newList, setNewList] = useState<Array<{ id: string; name: string }>>(
    []
  );

  const updateUserTagMutation = updateUserTag(orgId, userId);
  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    tag: IWorkspaceTag
  ) => {
    const isChecked = e.target.checked;
    const Check = allTags.some(allTag => allTag.id === tag.id);
    const newSelectedTags = isChecked
      ? [...selectedTags, { id: tag.id, name: tag.name }]
      : selectedTags.filter(selectedTag => selectedTag.id !== tag.id);
    setValue('tags', newSelectedTags);

    // switch to handle deleteList and newList updates
    switch (true) {
      case Check && !isChecked:
        setDeleteList(prev => [...prev, { id: tag.id, name: tag.name }]);
        break;
      case Check && isChecked:
        setDeleteList(prev => prev.filter(item => item.id !== tag.id));
        break;
      case !Check && isChecked:
        setNewList(prev => [...prev, { id: tag.id, name: tag.name }]);
        break;
      case !Check && !isChecked:
        setNewList(prev => prev.filter(item => item.id !== tag.id));
        break;
      default:
        break;
    }
  };

  const onSubmit = async () => {
    const newTags = selectedTags?.map(data => data.id);
    const res = await updateUserTagMutation.mutateAsync({
      tags: newTags?.length ? newTags : []
    });

    const toastTitle = [
      newList?.length > 0 &&
        `${newList.length} Tag${newList.length !== 1 ? 's' : ''} added`,
      deleteList?.length > 0 &&
        `${deleteList.length} Tag${deleteList.length !== 1 ? 's' : ''} deleted`
    ]
      .filter(Boolean)
      .join(', ');

    if (res.isSuccess) {
      toast({
        variant: 'success',
        title: toastTitle
      });

      return onClose();
    }

    return toast({
      variant: 'error',
      title: res.message
    });
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="1000px"
      closeButtonProps={{
        hidden: true
      }}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <ListView>
          <ListView.Title>
            <Flex
              flexDirection="column"
              alignItems="start"
              marginTop={4}
              gap={2}
            >
              <Text textStyle="headingSection">
                {allTags?.length ? 'Update Tags to User' : 'Add Tags to User'}
              </Text>
              <Text textStyle="textContext">
                Select any relevant tags to classify this user.
              </Text>
            </Flex>
          </ListView.Title>
          <ListView.Body>
            {data?.length ? (
              <Table>
                <Table.HeaderRow gap={4}>
                  <Table.HeaderCell flex="1" />
                  <Table.HeaderCell flex="8">Name</Table.HeaderCell>
                  <Table.HeaderCell flex="4" textAlign="center">
                    Scope
                  </Table.HeaderCell>
                  <Table.HeaderCell flex="8">Description</Table.HeaderCell>
                  <Table.HeaderCell flex="4">Category</Table.HeaderCell>
                  <Table.HeaderCell flex="8">
                    Category Description
                  </Table.HeaderCell>
                  <Table.HeaderCell flex="4">Category Color</Table.HeaderCell>
                </Table.HeaderRow>
                <Table.Body>
                  {data?.length ? (
                    data.map(tag => (
                      <Table.Row key={tag.id} gap={4}>
                        <Table.RowCell flex="1" textStyle="headingLine">
                          <Controller
                            name="tags"
                            control={control}
                            render={() => (
                              <Checkbox
                                id={tag.name}
                                name={tag.name}
                                ariaLabel={`Select ${tag.name}`}
                                isChecked={selectedTags?.some(
                                  selectedTag => selectedTag.id === tag.id
                                )}
                                onChange={e => handleCheckboxChange(e, tag)}
                              >
                                {''}
                              </Checkbox>
                            )}
                          />
                        </Table.RowCell>
                        <Table.RowCell flex="8" textStyle="headingLine">
                          <Badge size="context" variant="neutral">
                            <Flex
                              gap={1}
                              alignItems="center"
                              color={'neutralSecondaryContent'}
                            >
                              <FontAwesomeIcon icon={faUser} />
                              <Tooltip
                                hasArrow
                                borderRadius={4}
                                placement="top"
                                label={tag.name}
                              >
                                <Text
                                  textStyle="textContext"
                                  noOfLines={1}
                                  isTruncated
                                  wordBreak={'break-word'}
                                  whiteSpace={'normal'}
                                >
                                  {tag.name}
                                </Text>
                              </Tooltip>
                            </Flex>
                          </Badge>
                        </Table.RowCell>
                        <Table.RowCell flex="4" textAlign="center">
                          <Tooltip
                            hasArrow
                            borderRadius={4}
                            placement="top"
                            label={tag.journal?.name || 'Global'}
                          >
                            <Text
                              textStyle="textContext"
                              noOfLines={1}
                              whiteSpace={'break-spaces'}
                            >
                              {tag.journal?.shortname
                                ? tag.journal.shortname.toUpperCase()
                                : 'Global'}
                            </Text>
                          </Tooltip>
                        </Table.RowCell>
                        <Table.RowCell
                          flex="8"
                          textStyle="headingLine"
                          isTruncated
                        >
                          <Tooltip
                            hasArrow
                            borderRadius={4}
                            placement="top"
                            label={tag.description}
                          >
                            <Box width="100%" overflow="hidden">
                              <Text textStyle="textBody" isTruncated>
                                {tag.description}
                              </Text>
                            </Box>
                          </Tooltip>
                        </Table.RowCell>
                        <Table.RowCell flex="4" isTruncated>
                          <Tooltip
                            hasArrow
                            borderRadius={4}
                            placement="top"
                            label={tag.tagCategory}
                          >
                            <Text noOfLines={1} isTruncated>
                              {tag.tagCategory}
                            </Text>
                          </Tooltip>
                        </Table.RowCell>

                        <Table.RowCell
                          flex="8"
                          textStyle="headingLine"
                          isTruncated
                        >
                          <Tooltip
                            hasArrow
                            borderRadius={8}
                            placement="top"
                            label={tag.tagDescription}
                          >
                            <Box width="100%" overflow="hidden">
                              <Text textStyle="textBody" isTruncated>
                                {tag.tagDescription}
                              </Text>
                            </Box>
                          </Tooltip>
                        </Table.RowCell>
                        <Table.RowCell
                          flex="4"
                          textStyle="headingLine"
                          isTruncated
                        >
                          <Tooltip
                            hasArrow
                            borderRadius={4}
                            placement="top"
                            label={tag.tagColor}
                          >
                            <Box
                              w="30px"
                              h="30px"
                              bg={tag.tagColor}
                              border="1px solid"
                              borderColor={'gray.300'}
                              borderRadius="4px"
                              shadow={'sm'}
                            />
                          </Tooltip>
                        </Table.RowCell>
                      </Table.Row>
                    ))
                  ) : (
                    <ListLoader />
                  )}
                </Table.Body>
              </Table>
            ) : (
              <>
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  backgroundColor="neutralSecondaryFill"
                  borderRadius="small"
                  height="112px"
                >
                  <FontAwesomeIcon icon={faTag} size="xl" />
                </Flex>
                <Flex flexDirection="column" alignItems="center" marginTop={4}>
                  <Text textStyle="headingGroup">No Results Found</Text>
                  <Text>
                    Please create the required tags or contact the Org Admin to
                    create the relevant tags for this journal.
                  </Text>
                </Flex>
              </>
            )}
          </ListView.Body>
          <Flex
            justifyContent="flex-end"
            gap={2}
            alignItems="center"
            marginTop={4}
            width="100%"
          >
            <Button onClick={onClose} variant="secondary">
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              isDisabled={
                !(data && data.length) ||
                (!deleteList.length && !newList.length)
              }
              disabled={
                !(data && data.length) ||
                (!deleteList.length && !newList.length)
              }
            >
              {allTags?.length ? 'Update Selected Tags' : 'Add Selected Tags'}
            </Button>
          </Flex>
        </ListView>
      </form>
    </Modal>
  );
};

export default AddingUserTagsModal;
